import React from 'react';
import { Shield, Lock, Eye, FileText, Mail, Phone, Globe, CheckCircle, AlertCircle, Settings, TrendingUp, Download, Edit2, Trash2, Database, UserCheck, Bell } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-pink-50/30">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-12 pt-24">
        {/* Icon + Title */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
            <Shield className="w-7 h-7 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-purple-700">
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm mb-1">Last Updated: March 2026</p>
          <p className="text-gray-500 text-sm">Compliant with IT Act, 2000 &amp; IT (Reasonable Security Practices) Rules, 2011</p>
        </div>

        {/* Data Protection Commitment */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8 flex items-start gap-3">
          <Lock className="w-5 h-5 text-blue-700 flex-shrink-0 mt-1" />
          <div>
            <p className="font-bold text-blue-800 mb-1">Data Protection Commitment</p>
            <p className="text-blue-800 text-sm leading-relaxed">
              SET INDIA BUSINESS PVT LTD ("KoPartner") is committed to protecting your privacy in accordance with the Information Technology Act, 2000, Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and other applicable Indian laws.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* 1. Data Controller */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              1. Data Controller
            </h2>
            <p className="text-gray-700 mb-3">
              The data controller responsible for your personal information is:
            </p>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="font-semibold text-gray-800">SET INDIA BUSINESS PVT LTD</p>
              <p className="text-gray-600 text-sm">Website: www.kopartner.in</p>
              <p className="text-gray-600 text-sm">Data Protection Officer: dpo@kopartner.in</p>
              <p className="text-gray-600 text-sm">Grievance Officer: grievance@kopartner.in</p>
            </div>
          </section>

          {/* 2. Information We Collect */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              2. Information We Collect
            </h2>

            <h3 className="font-semibold text-gray-800 mb-2">2.1 Personal Information (Collected with Consent)</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm mb-5 pl-1">
              <li>Full name, email address, mobile number</li>
              <li>Profile photograph and bio/description</li>
              <li>City, pin code, and service area</li>
              <li>Date of birth and gender (for age verification)</li>
              <li>Government-issued ID (KoPartners only, for verification)</li>
              <li>Bank account details (KoPartners only, for payouts)</li>
            </ul>

            <h3 className="font-semibold text-gray-800 mb-2">2.2 Sensitive Personal Data or Information (SPDI)</h3>
            <p className="text-gray-600 text-sm mb-2">As defined under IT (Reasonable Security Practices) Rules, 2011:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm mb-4 pl-1">
              <li>Financial information (bank account for payouts - KoPartners only)</li>
              <li>Biometric data (facial recognition for verification)</li>
              <li>Government ID numbers (for KYC verification)</li>
            </ul>

            <div className="bg-green-50 border border-green-200 p-4 rounded-xl mb-5">
              <p className="text-green-800 text-sm">
                <span className="font-bold">IMPORTANT:</span> We do NOT collect or store: Payment card numbers, CVV, UPI PIN, internet banking credentials, or any payment authentication data.
              </p>
            </div>

            <h3 className="font-semibold text-gray-800 mb-2">2.3 Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li>Device information (type, OS, browser)</li>
              <li>IP address and approximate geolocation</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          {/* 3. Purpose of Data Collection */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              3. Purpose of Data Collection
            </h2>
            <p className="text-gray-700 mb-3">We collect and process your information for the following lawful purposes:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm pl-1">
              <li><strong>Service Delivery:</strong> To facilitate connections between Clients and KoPartners</li>
              <li><strong>Identity Verification:</strong> To verify user identities and ensure platform safety</li>
              <li><strong>Payment Processing:</strong> To verify payments and process payouts to KoPartners</li>
              <li><strong>Communication:</strong> To send booking confirmations, updates, and support messages</li>
              <li><strong>Safety &amp; Security:</strong> To detect fraud, prevent abuse, and ensure user safety</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and legal requests</li>
              <li><strong>Platform Improvement:</strong> To analyze usage and improve our services</li>
            </ul>
          </section>

          {/* 4. Legal Basis for Processing - purple bordered box */}
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-purple-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-700" />
              4. Legal Basis for Processing
            </h2>
            <p className="text-purple-900 mb-3 text-sm">We process your data based on:</p>
            <ul className="list-disc list-inside space-y-1 text-purple-900 text-sm pl-1">
              <li><strong>Consent:</strong> Explicit consent obtained at registration (Section 43A, IT Act)</li>
              <li><strong>Contract:</strong> Performance of our Terms of Service agreement</li>
              <li><strong>Legal Obligation:</strong> Compliance with Indian laws and regulations</li>
              <li><strong>Legitimate Interests:</strong> Platform security, fraud prevention, service improvement</li>
            </ul>
            <p className="text-purple-900 text-sm mt-4">
              You may withdraw consent at any time by contacting privacy@kopartner.in
            </p>
          </div>

          {/* 5. Information Sharing */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-600" />
              5. Information Sharing &amp; Disclosure
            </h2>
            <p className="text-gray-700 mb-3">We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm pl-1">
              <li><strong>Other Users:</strong> Profile information visible to potential Clients/KoPartners as needed for service delivery</li>
              <li><strong>Service Providers:</strong> Third-party vendors assisting in platform operations (under confidentiality agreements)</li>
              <li><strong>Payment Processors:</strong> For verifying payments (no card data shared)</li>
              <li><strong>Government Authorities:</strong> When required by law, court order, or legal process</li>
              <li><strong>Law Enforcement:</strong> To prevent crime, protect safety, or respond to emergencies</li>
            </ul>
            <div className="mt-4 bg-green-50 border border-green-200 p-4 rounded-xl">
              <p className="text-green-800 text-sm font-semibold">
                We NEVER sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </div>
          </section>

          {/* 6. Data Security Measures - green bordered box */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-green-800 flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-700" />
              6. Data Security Measures
            </h2>
            <p className="text-green-900 text-sm mb-3">In compliance with IT (Reasonable Security Practices) Rules, 2011, we implement:</p>
            <ul className="list-disc list-inside space-y-1 text-green-900 text-sm pl-1">
              <li>SSL/TLS encryption for all data in transit</li>
              <li>AES-256 encryption for sensitive data at rest</li>
              <li>Multi-factor authentication for sensitive operations</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and role-based permissions</li>
              <li>Intrusion detection and prevention systems</li>
              <li>Secure cloud infrastructure with certified providers</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="text-green-800 text-sm mt-4">
              While we implement industry-standard security measures, no system is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </div>

          {/* 7. Data Retention */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              7. Data Retention
            </h2>
            <p className="text-gray-700 mb-3">We retain your data for:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li><strong>Active Accounts:</strong> As long as your account is active</li>
              <li><strong>Post-Deletion:</strong> Up to 180 days for backup/recovery purposes</li>
              <li><strong>Legal Requirements:</strong> Up to 8 years for financial/tax records</li>
              <li><strong>Dispute Resolution:</strong> Until resolution plus limitation period</li>
            </ul>
            <p className="text-gray-600 text-sm mt-4">
              Upon account deletion, we will delete or anonymize your data within 90 days, except where retention is required by law.
            </p>
          </section>

          {/* 8. Your Rights */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-purple-600" />
              8. Your Rights
            </h2>
            <p className="text-gray-700 mb-3">Under applicable Indian law, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
              <li><strong>Data Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Object:</strong> Object to certain types of processing</li>
              <li><strong>Complain:</strong> Lodge a complaint with relevant authorities</li>
            </ul>
            <p className="text-gray-600 text-sm mt-4">
              To exercise these rights, contact: privacy@kopartner.in
            </p>
          </section>

          {/* 9. Cookies & Tracking Technologies */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900 flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-600" />
              9. Cookies &amp; Tracking Technologies
            </h2>
            <p className="text-gray-700 mb-3">We use cookies for:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm pl-1">
              <li><strong>Essential:</strong> Login sessions, security tokens</li>
              <li><strong>Functional:</strong> Preferences, language settings</li>
              <li><strong>Analytics:</strong> Usage patterns, platform improvement</li>
              <li><strong>Security:</strong> Fraud detection, abuse prevention</li>
            </ul>
            <p className="text-gray-600 text-sm mt-4">
              You can manage cookie preferences through your browser. Disabling cookies may affect functionality.
            </p>
          </section>

          {/* 10. Children's Privacy - red bordered box */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              10. Children's Privacy
            </h2>
            <p className="text-red-800 text-sm leading-relaxed">
              KoPartner is NOT intended for use by persons under 18 years of age. We do not knowingly collect personal information from minors. If we discover that a minor has provided us with personal information, we will delete it immediately. If you believe a minor has provided us with personal data, contact: privacy@kopartner.in
            </p>
          </div>

          {/* 11. Changes to Privacy Policy */}
          <section className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900">11. Changes to Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of material changes through the Platform or via email. Continued use after changes constitutes acceptance.
            </p>
          </section>

          {/* 12. Contact & Grievance - light gray box */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-xl font-bold mb-4 text-gray-900">12. Contact &amp; Grievance</h2>
            <p className="font-semibold text-gray-800 mb-2">SET INDIA BUSINESS PVT LTD</p>
            <p className="text-gray-700 text-sm">Privacy Officer: privacy@kopartner.in</p>
            <p className="text-gray-700 text-sm">Data Protection Officer: dpo@kopartner.in</p>
            <p className="text-gray-700 text-sm">Grievance Officer: grievance@kopartner.in</p>
            <p className="text-gray-700 text-sm">Website: www.kopartner.in</p>
            <p className="text-gray-600 text-sm mt-3">Response time: Within 30 days of receiving your request.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
